import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type DeclaredInputsSummary,
  type JobRecord,
  type JobSort,
  type PollSummary,
  declareAnswer as declareAnswerRequest,
  getJobs,
  markJobsSeen,
  pollWatchlist,
  searchJobs,
  updateJobBucket,
} from './api';
import { UNDETERMINED_BUCKET_KEY, type JobBucket } from './jobBuckets';
import { readJobsInboxCache, writeJobsInboxCache } from './jobsInboxCache';

/** D19 — the honest "nothing declared yet" default, mirrored from the
 *  server's own missingRequiredAnswers OR-gate (DeclaredAnswersService.ts):
 *  both required keys read as missing until the FIRST GET /jobs response
 *  lands (never assumed satisfied before a real read confirms it). */
const INITIAL_DECLARED_INPUTS: DeclaredInputsSummary = {
  missingInputs: ['home_country', 'home_postal_code'],
  sortCriteria: '',
};

/** W14 (docs/WALK-FINDINGS-2026-07-21.md) — the sort this hook always mounts
 *  with. Named so the mount-time cache lookup below asks for EXACTLY the
 *  same sort the fresh `useState<JobSort>` call two lines down also starts
 *  at, rather than two separately-typed 'fit' literals silently drifting
 *  apart. */
const INITIAL_SORT: JobSort = 'fit';

/**
 * The Discover/Jobs tab's data hook — the Throughline analogue of
 * apps/web/src/lib/useJobs.ts's useJobs, scoped to exactly what this brief
 * needs: the inbox read (GET /jobs, sortable), a search mode (POST
 * /jobs/search) that swaps the rendered list without discarding the inbox
 * underneath, and a "poll now" action (POST /watchlist/poll) with the
 * server's OWN honest summary — never a client-invented count.
 *
 * Search and inbox are DELIBERATELY kept as separate result sets (not merged)
 * so leaving the search box empty always shows the true inbox again, and a
 * search never silently mutates what GET /jobs would have returned.
 */

export interface JobsInboxState {
  /** The GET /jobs result — always the full inbox, independent of the active
   *  bucket filter (filtering is a pure client-side view over this, see
   *  jobBuckets.ts's filterJobsByBucket) or of an active search. */
  jobs: JobRecord[];
  loading: boolean;
  error: string | null;
  /** D19 — the SERVED missing-input banner + criteria-transparency line.
   *  Never re-derived client-side (no-frontend-bandaids law) — this is
   *  exactly what GET /jobs's own declaredInputs field carried this fetch. */
  declaredInputs: DeclaredInputsSummary;
}

/** D19/D17 — the ask-unit's write half, as hook state. 'idle' before any
 *  declare attempt this session; 'saving' while the POST is in flight;
 *  'error' on a failed declare (message carried, never silent); the state
 *  returns to 'idle' on a successful declare (the banner itself disappears
 *  once the follow-up refresh's declaredInputs comes back satisfied — no
 *  separate "success" flash needed, the honest absence of the banner IS the
 *  confirmation). */
export interface DeclareAnswerState {
  status: 'idle' | 'saving' | 'error';
  error: string | null;
}

export interface SearchState {
  active: boolean;
  loading: boolean;
  error: string | null;
  query: string;
  results: JobRecord[];
  /** The honest sweep summary IF this search ran with sweepFirst — null
   *  otherwise, never a synthesized "0 found." */
  sweep: PollSummary | null;
}

const INITIAL_SEARCH_STATE: SearchState = {
  active: false,
  loading: false,
  error: null,
  query: '',
  results: [],
  sweep: null,
};

export type PollPhase = 'idle' | 'polling' | 'done' | 'error';

export interface PollState {
  phase: PollPhase;
  /** The server's own PollSummary from the last completed poll — the ONLY
   *  source of any count this hook renders. Null before the first poll. */
  summary: PollSummary | null;
  error: string | null;
}

/** Exported (additive — no behavior change) so useWelcomeBackPoll.ts (Home's
 *  own "CHECK NOW" affordance, LANE U1) can reuse the exact same idle-state
 *  shape rather than redeclaring an equivalent literal — one source of truth
 *  for what "no poll has run yet" looks like. */
export const INITIAL_POLL_STATE: PollState = { phase: 'idle', summary: null, error: null };

/** BOARD-TRUTH FIX (owner report: "I think my dream job is gone") — the
 *  honest failure state for a bucket-pick PATCH. Before this fix, setJobBucket
 *  had NO try/catch: a rejected PATCH (404 on an id class the endpoint
 *  doesn't recognise, a lapsed session, a network blip) propagated as an
 *  unhandled promise rejection. BucketPicker's own try/finally re-enabled the
 *  control (so the UI never looked "stuck"), but nothing ever told the user
 *  the pick had NOT persisted — the select's DOM value had already flipped
 *  optimistically on click, then silently reverted to the server's true
 *  bucket on the next refresh, with zero explanation. Keyed by jobId (not a
 *  single global error) so a failure on one card is never misattributed to a
 *  different card the user picks next. */
export interface BucketErrorState {
  jobId: string;
  message: string;
}

export function useJobsInbox() {
  const [sort, setSort] = useState<JobSort>(INITIAL_SORT);
  // W14 — read the session-scoped cache exactly ONCE per mount (useState's
  // lazy-initializer form is the one React guarantees only runs on the
  // FIRST render, unlike a plain call in the component body which would
  // re-read on every render). A hit means this mount is a back-nav return
  // (or any other remount within the same tab session), not a genuinely
  // first load — jobsInboxCache.ts's own header has the full W14 diagnosis.
  const [initialCache] = useState(() => readJobsInboxCache(INITIAL_SORT));
  const [state, setState] = useState<JobsInboxState>(() =>
    initialCache
      ? {
          jobs: initialCache.jobs,
          loading: false,
          error: null,
          declaredInputs: initialCache.declaredInputs,
        }
      : { jobs: [], loading: true, error: null, declaredInputs: INITIAL_DECLARED_INPUTS },
  );
  // Consumed by the mount effect below: a cache hit means the FIRST run of
  // that effect should render what's already in `state` above instead of
  // firing a redundant network refetch — every LATER run (a real `sort`
  // change, which recreates `refresh` and re-triggers the effect) is
  // unaffected, since this flag is flipped false the moment it's read once.
  const skipNextAutoRefresh = useRef(initialCache !== null);
  const [bucketFilter, setBucketFilter] = useState<JobBucket | typeof UNDETERMINED_BUCKET_KEY | 'all'>('all');
  const [search, setSearch] = useState<SearchState>(INITIAL_SEARCH_STATE);
  const [poll, setPoll] = useState<PollState>(INITIAL_POLL_STATE);
  // BOARD-TRUTH FIX — the honest bucket-PATCH failure state (see
  // BucketErrorState's own doc comment above). null when no bucket write has
  // ever failed, or the most recent failure has been cleared by a subsequent
  // successful pick on the SAME job.
  const [bucketError, setBucketError] = useState<BucketErrorState | null>(null);
  // D19/D17 — the declare-answer ask-unit's own request state.
  const [declareState, setDeclareState] = useState<DeclareAnswerState>({ status: 'idle', error: null });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await getJobs(sort);
      const declaredInputs = result.declaredInputs ?? INITIAL_DECLARED_INPUTS;
      setState({
        jobs: result.jobs,
        loading: false,
        error: null,
        // Optional on the wire type (older/unrelated call sites that share
        // getJobs' return shape via ReturnType<> don't populate it) — the
        // honest "nothing declared yet" default stands until a real response
        // carries the field.
        declaredInputs,
      });
      // W14 — every real served inbox result refreshes the session-scoped
      // cache: the first-load fetch, a sort change, declareAnswer's own
      // follow-up refresh, AND runPollNow's "Check for new jobs" refresh all
      // flow through here, so a later mount (back-nav) can hydrate from
      // whichever of those was most recent. His spec ("refreshed manually
      // once logged in the first time") is about the PASSIVE mount refetch
      // this hook's own mount effect used to always fire — it does not mean
      // every OTHER already-explicit, user-driven refresh trigger stops
      // keeping the list honest; those still exist and now additionally
      // leave the next back-nav instant too.
      writeJobsInboxCache({ sort, jobs: result.jobs, declaredInputs });
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      }));
    }
  }, [sort]);

  /** D19/D17 — the ask-unit's write half. Persists ONE declared answer, then
   *  re-fetches the inbox (declaredAnswer's server-side wake events —
   *  the per-user bucket resweep + the keystone-hub write — have already
   *  landed by the time this POST resolves, so the follow-up refresh() picks
   *  up freshly re-derived buckets AND the now-satisfied banner state in one
   *  round trip, never a second manual step). */
  const declareAnswer = useCallback(
    async (questionKey: string, answer: string) => {
      setDeclareState({ status: 'saving', error: null });
      try {
        await declareAnswerRequest(questionKey, answer);
        setDeclareState({ status: 'idle', error: null });
        await refresh();
      } catch (err) {
        setDeclareState({
          status: 'error',
          error: err instanceof Error ? err.message : "Couldn't save that — try again.",
        });
      }
    },
    [refresh],
  );

  useEffect(() => {
    // W14 — a cache hit already hydrated `state` synchronously above; skip
    // ONLY this first run so a fresh mount never flashes "All 0 · Loading
    // your jobs…" only to re-render the exact same list a beat later. Any
    // LATER run of this same effect (sort changes, since `refresh` is
    // recreated with a new `sort` in its closure) is a real, explicit,
    // user-driven parameter change — the server does the actual sorting
    // (no-frontend-bandaids law), so that always still fetches.
    if (skipNextAutoRefresh.current) {
      skipNextAutoRefresh.current = false;
      return;
    }
    refresh();
  }, [refresh]);

  // QA #34 — mark this visit as "seen" once, on mount (never per-poll, never
  // per-refresh — a single real visit is what resets the new-for-review
  // clock). Fire-and-forget: this is bookkeeping, not user-facing data, so a
  // failure here must never surface as a Jobs-tab error — the NEXT visit
  // simply tries again. Deliberately does NOT block or gate the initial
  // `refresh()` above (the list renders immediately either way); it also
  // does not re-run `refresh()` itself, since jobs already in flight from THIS
  // load should still show their new-for-review badge for the visit that is
  // happening right now — the badge only clears on the FOLLOWING visit.
  useEffect(() => {
    markJobsSeen().catch(() => {
      /* best-effort — see comment above */
    });
    // Deliberately an empty dep array — this fires exactly once per mount
    // (once per real visit to the tab), never on every sort/bucket-filter
    // re-render.
  }, []);

  const runSearch = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) {
      // Empty query clears search mode — the caller falls back to the inbox.
      setSearch(INITIAL_SEARCH_STATE);
      return;
    }
    setSearch((s) => ({ ...s, active: true, loading: true, error: null, query: trimmed }));
    try {
      const result = await searchJobs({ query: trimmed, filters: bucketFilterInput(bucketFilter) });
      setSearch({
        active: true,
        loading: false,
        error: null,
        query: trimmed,
        results: result.jobs,
        sweep: result.sweep,
      });
    } catch (err) {
      setSearch((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      }));
    }
  }, [bucketFilter]);

  const clearSearch = useCallback(() => setSearch(INITIAL_SEARCH_STATE), []);

  /** LANE W2.1-BUCKETS — the user-sort action: PATCH /jobs/:id/bucket, then
   *  reflect the new bucket in BOTH lists this hook holds (the full inbox
   *  AND the active search results, if any) so a card the user just re-sorted
   *  updates in place rather than requiring a manual refresh. Optimistic:
   *  the local state updates immediately on a successful server response
   *  (never before — a failed PATCH must not show a bucket the server never
   *  actually persisted). Thin: the terminal-override marking
   *  (bucket_source='user') happens entirely server-side; this hook only
   *  reflects the RESULT.
   *
   *  BOARD-TRUTH FIX (owner report: "I think my dream job is gone") — this
   *  used to have NO try/catch: a rejected PATCH became an unhandled promise
   *  rejection, the pick silently never persisted, and the card's select
   *  quietly snapped back to the server's true (unchanged) bucket on the
   *  next refresh with zero explanation. Now: a failure clears bucketError to
   *  a real, jobId-scoped message (never a silent revert) and the job list is
   *  left UNTOUCHED (no optimistic bucket applied) so BucketPicker's own
   *  controlled `value` — driven by the job prop, not local state — correctly
   *  reflects what is actually stored, not what was attempted. A SUCCESSFUL
   *  pick on any job clears a stale bucketError for that same job (never for
   *  a different job's error, so two cards' failures can't cross-clear each
   *  other). Rethrows on failure too (BucketPicker's own finally still needs
   *  to run to re-enable the control) — callers that want the error message
   *  read bucketError from this hook's return value, not the rejection
   *  itself. */
  const setJobBucket = useCallback(async (jobId: string, bucket: JobBucket) => {
    try {
      const result = await updateJobBucket(jobId, bucket);
      const applyBucket = (list: JobRecord[]): JobRecord[] =>
        list.map((j) => (j.id === jobId ? { ...j, bucket: result.bucket } : j));
      setState((s) => ({ ...s, jobs: applyBucket(s.jobs) }));
      setSearch((s) => ({ ...s, results: applyBucket(s.results) }));
      setBucketError((e) => (e?.jobId === jobId ? null : e));
      return result;
    } catch (err) {
      setBucketError({
        jobId,
        message:
          err instanceof Error
            ? err.message
            : "Couldn't save that bucket — try again.",
      });
      throw err;
    }
  }, []);

  /** Dismisses the current bucket-write error (e.g. once the user has read
   *  it, or tries again and it clears itself via a success above). */
  const clearBucketError = useCallback(() => setBucketError(null), []);

  const runPollNow = useCallback(async () => {
    setPoll({ phase: 'polling', summary: null, error: null });
    try {
      const { ok: _ok, ...summary } = await pollWatchlist();
      setPoll({ phase: 'done', summary, error: null });
      // A poll can surface newly-ingested jobs — refresh the inbox (and the
      // active search, if any) so the served list reflects what was just found.
      await refresh();
      if (search.active) await runSearch(search.query);
    } catch (err) {
      setPoll({
        phase: 'error',
        summary: null,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, [refresh, runSearch, search.active, search.query]);

  return {
    sort,
    setSort,
    state,
    refresh,
    bucketFilter,
    setBucketFilter,
    setJobBucket,
    bucketError,
    clearBucketError,
    search,
    runSearch,
    clearSearch,
    poll,
    runPollNow,
    declareAnswer,
    declareState,
  };
}

/** POST /jobs/search's `filters.bucket` accepts exactly one bucket — the
 *  client's 'all'/'undetermined' view states have no server-side equivalent
 *  (undetermined jobs are a client-only grouping, see jobBuckets.ts's
 *  UNDETERMINED_BUCKET_KEY doc comment), so both map to "no bucket filter"
 *  rather than sending a value the zod schema would reject. */
function bucketFilterInput(
  bucket: JobBucket | typeof UNDETERMINED_BUCKET_KEY | 'all',
): { bucket: JobBucket } | undefined {
  if (bucket === 'all' || bucket === UNDETERMINED_BUCKET_KEY) return undefined;
  return { bucket };
}
